import React from 'react'
import { styled } from 'styled-components'
import { Link } from 'react-router-dom'
import useScreenSize from '../../../hooks/useScreenSize'
import useChangeSlider from '../../../hooks/useChangeSlides'

const WorkshopHero = ({
    images,
    titleLines = [],
    accentLine,
    meta = [],
    primaryCta,
    secondaryCta,
}) => {
    const screenSize = useScreenSize();
    const [currentSlide, setSlideIndex] = useChangeSlider(images.length * 1000);

    return (
        <Container className='hero'>
            <SliderContainer $height={screenSize.height}>
                <SlideTrack>
                    {images.map((src, index) => (
                        <Slide
                            key={index}
                            $isActive={index + 1 === currentSlide}
                            style={{ backgroundImage: `url(${src})` }}
                        />
                    ))}
                </SlideTrack>

                <Overlay>
                    <HeroInner>
                        <HeroTitle>
                            {titleLines.map((line, i) => (
                                <React.Fragment key={i}>
                                    {line}<br />
                                </React.Fragment>
                            ))}
                            {accentLine && <TitleAccent>{accentLine}</TitleAccent>}
                    </HeroTitle>

                    
                    {meta.length > 0 && (
                            <MetaRow>
                                {meta.map((item, i) => {
                                    const Icon = item.icon;
                                    return (
                                        <React.Fragment key={item.label}>
                                            <MetaItem>
                                                <MetaIcon>
                                                    <Icon size={14} />
                                                </MetaIcon>
                                                {item.label}
                                            </MetaItem>
                                            {i < meta.length - 1 && <MetaDivider />}
                                        </React.Fragment>
                                    );
                                })}
                            </MetaRow>
                        )}

                        {(primaryCta || secondaryCta) && (
                        <CTARow>
                            {primaryCta && (
                            <Link to={primaryCta.to}>
                                <RegisterBtn>{primaryCta.label}</RegisterBtn>
                            </Link>)}
                            
                            {secondaryCta && (
                            <LearnMoreBtn onClick={secondaryCta.onClick}>
                                {secondaryCta.label}
                            </LearnMoreBtn>)}
                        </CTARow> )}
                    </HeroInner>
                </Overlay>

                <RadioButtons>
                    {images.map((_, i) => (
                        <input
                            key={i}
                            type="radio"
                            name="radio-btn"
                            id={`radio${i + 1}`}
                            checked={currentSlide === i}
                            onChange={() => setSlideIndex(i + 1)}
                            readOnly
                            style={{ display: 'none' }}
                        />
                    ))}

                    <div className="navigation-dots">
                        {images.map((_, i) => {
                            const num = i + 1;
                            return (
                                <label
                                    key={num}
                                    htmlFor={`radio${num}`}
                                    className={`dot ${currentSlide === num ? 'active' : ''}`}
                                    onClick={() => setSlideIndex(num)}
                                />
                            );
                        })}
                    </div>
                </RadioButtons>
            </SliderContainer>
        </Container>
    );
};

const SliderContainer = styled.div`
    width: 100%;
    height: ${props => props.$height}px;
    overflow: hidden;
    position: relative;
`;

const SlideTrack = styled.div`
    position: absolute;
    inset: 0;
`;

const Slide = styled.div`
    position: absolute;
    inset: 0;
    opacity: ${props => (props.$isActive ? 1 : 0)};
    transition: opacity 0.8s ease-in-out;
    background-size: cover;
    background-position: center;
`;

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(
        105deg,
        rgba(10, 30, 55, 0.82) 0%,
        rgba(10, 30, 55, 0.60) 5%,
        rgba(10, 30, 55, 0.15) 100%
    );
    display: flex;
    align-items: center;
`;

const HeroInner = styled.div`
    padding: 0 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 720px;
    animation: fadeUp 1s ease-out both;

    @keyframes fadeUp {
        from { opacity: 0; transform: translateY(24px); }
        to   { opacity: 1; transform: translateY(0); }
    }

    @media (min-width: 800px) {
        padding: 0 4rem;
    }
`;



const HeroTitle = styled.h1`
    font-family: "PT Serif", serif;
    font-size: clamp(2rem, 5vw, 3.4rem);
    font-weight: 700;
    color: #ffffff;
    line-height: 1.15;
    letter-spacing: -0.01em;
    margin: 0;
`;

const TitleAccent = styled.span`
    color: #f0b429;
`;

const MetaRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.82rem;
    font-weight: 400;
`;

const MetaItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const MetaIcon = styled.span`
    display: flex;
    align-items: center;
    color: #f0b429;
    flex-shrink: 0;
`;

const MetaDivider = styled.span`
    width: 1px;
    height: 14px;
    background: rgba(255, 255, 255, 0.25);
`;

const CTARow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-top: 0.5rem;
`;

const RegisterBtn = styled.button`
    background: #c8870a;
    color: #ffffff;
    border: none;
    width: 15em;
    padding: 0.85em 2.2em;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: #a86e08;
    }
`;

const LearnMoreBtn = styled.button`
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.3);
    width: 15em;
    justify-content : center;
    padding: 0.85em 1.8em;
    font-size: 0.85rem;
    font-weight: 400;
    letter-spacing: 0.06em;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
        color: #ffffff;
        border-color: rgba(255, 255, 255, 0.6);
    }
`;

const Container = styled.main`
    position: relative;
    width: 100%;
    height: 90%;
    animation: heroLoad 1.8s ease-in-out;
    z-index: 2;

    @keyframes heroLoad {
        from { clip-path: polygon(0 0, 100% 0, 100% 0, 0 0); }
        to   { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
    }
`;

const RadioButtons = styled.div`
    position: absolute;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    input[type="radio"] { display: none; }

    .navigation-dots {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.6);
        background: transparent;
        cursor: pointer;
        transition: all 0.3s ease;

        &.active {
            background: #f0b429;
            border-color: #f0b429;
            width: 24px;
            border-radius: 4px;
        }

        &:hover {
            background: rgba(255, 255, 255, 0.4);
        }
    }
`;

export default WorkshopHero;